/**
 * ReportDisambiguationStep — second stage of NewReportWizard, shown only
 * when a prior report of the same type already exists for this site
 * (Workflow Prompt 5).
 *
 * Three options:
 *   - update      → new version on the same lineage, prefilled prefs
 *   - replace     → generate new + delete old after success, prefilled prefs
 *   - add-another → fresh report, blank prefs (sibling lineage for A/B)
 */
import React from 'react';
import { ChevronLeft, ChevronRight, FileText, Leaf, Building2, Zap, Droplets, Cable, RefreshCw, ArrowLeftRight, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { StatusPill } from '../ui/status-pill';
import { getReportType, type ReportTypeId } from '../../config/reportTypes';
import type { SiteEntity } from '../../types/site';
import type { SiteRagReport } from '../../types/siteRag';
import type { WizardMode } from './ReportPreferencesStep';

const ICON_MAP: Record<string, React.ElementType> = { FileText, Leaf, Building2, Zap, Droplets, Cable };

interface Props {
  reportType: ReportTypeId;
  site: SiteEntity;
  /** The most-recent prior report of the same type for this site. */
  latest: SiteRagReport;
  /** How many prior reports exist on the same lineage (1 = just `latest`, 2+ = with version history). */
  lineageDepth: number;
  /**
   * Whether the requester may REPLACE `latest`. Replace-mode finalization
   * deletes the parent document, so the server refuses it for a report held
   * in another member's namespace (`PARENT_REPORT_MISMATCH`, 412). Offering
   * the option anyway guaranteed a dead-end rejection after the preferences
   * step; the caller passes `false` when `latest` belongs to another user.
   * Defaults to `true` so existing callers keep their behaviour.
   */
  replaceAllowed?: boolean;
  onBack: () => void;
  onChoose: (mode: Extract<WizardMode, 'update' | 'replace' | 'add-another'>) => void;
}

const OptionCard: React.FC<{
  title: string;
  blurb: string;
  icon: React.ElementType;
  onClick: () => void;
  recommended?: boolean;
}> = ({ title, blurb, icon: Icon, onClick, recommended }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left p-4 border-2 transition-colors flex items-start gap-4 ${
      recommended
        ? 'border-primary/40 bg-primary/[0.04] hover:bg-primary/10'
        : 'border-border-subtle bg-card hover:bg-state-hover'
    }`}
  >
    <div className={`w-9 h-9 border flex items-center justify-center shrink-0 ${
      recommended ? 'border-primary/40 bg-primary/15' : 'border-border-subtle bg-state-hover'
    }`}>
      <Icon className={`w-4 h-4 ${recommended ? 'text-primary' : 'text-text-secondary'}`} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-widest text-text-primary">{title}</span>
        {recommended && (
          <span className="type-meta font-bold uppercase tracking-widest px-1.5 py-0.5 bg-primary/15 text-primary rounded-md border border-primary/40">
            Recommended
          </span>
        )}
      </div>
      <p className="text-[10px] text-text-muted mt-1 leading-relaxed">{blurb}</p>
    </div>
    <ChevronRight className="w-4 h-4 text-text-muted shrink-0 mt-1" />
  </button>
);

export const ReportDisambiguationStep: React.FC<Props> = ({ reportType, site, latest, lineageDepth, replaceAllowed = true, onBack, onChoose }) => {
  const typeDef = getReportType(reportType);
  const TypeIcon = ICON_MAP[typeDef.icon] || FileText;
  const lastRunDate = new Date(latest.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  const versionLabel = `v${latest.version ?? 1}.${latest.minorVersion ?? 0}`;

  return (
    <div data-testid="report-disambiguation-body">
      {/* Per-step header — WizardFrame provides the wizard title +
           step strip; this header carries the step-specific context
           (icon + "you already have a … report" copy). The legacy
           per-step Back button is gone — WizardFrame's footer owns
           Back so cancel/back are consistent across steps. */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-start gap-3">
            <div className="w-10 h-10 border border-border bg-state-hover flex items-center justify-center shrink-0">
              <TypeIcon className="w-5 h-5 text-text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary">
                You already have a {typeDef.shortLabel} report for {site.name}.
              </h2>
              <div className="text-[10px] text-text-secondary mt-1">
                Last run {lastRunDate} ({versionLabel}{lineageDepth > 1 ? ` · ${lineageDepth} version${lineageDepth === 2 ? '' : 's'} on this lineage` : ''}).
                {' '}What would you like to do?
              </div>
            </div>
          </div>
        </div>

        {/* Three options */}
        <div className="p-5 space-y-3">
          <OptionCard
            recommended
            icon={RefreshCw}
            title="Update the existing report"
            blurb="Creates a new version on the same lineage. Old versions preserved. Best when site data has changed (utility update, permit shift, etc.)."
            onClick={() => onChoose('update')}
          />
          {replaceAllowed ? (
            <OptionCard
              icon={ArrowLeftRight}
              title="Replace the existing report"
              blurb="Generate a new report and delete the old one after success. Best when the previous report had errors and you want a clean slate."
              onClick={() => onChoose('replace')}
            />
          ) : (
            <div
              className="w-full text-left p-4 border-2 border-border-subtle bg-card/60 flex items-start gap-4 text-text-secondary"
              data-testid="report-disambiguation-replace-unavailable"
            >
              <div className="w-9 h-9 border border-border-subtle bg-state-hover flex items-center justify-center shrink-0">
                <ArrowLeftRight className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <StatusPill variant="neutral">Replace is not available</StatusPill>
                <div className="text-[11px] mt-1">
                  This report was generated by another member of your organisation. Only its author can replace it; choose Update to create a new version on the same lineage.
                </div>
              </div>
            </div>
          )}
          <OptionCard
            icon={Plus}
            title="Add another report alongside"
            blurb='Useful for A/B comparison (e.g. "with renewables" vs. "without"). Both reports stay on the site as siblings.'
            onClick={() => onChoose('add-another')}
          />
      </div>
      {/* Cancel/Back footer hoisted to WizardFrame.footer — see
           NewReportWizard.tsx for the shared Back/Cancel pair. */}
    </div>
  );
};
